import React, { useState } from 'react';
import { Search, SlidersHorizontal } from 'lucide-react';
import Header from './components/Header20';
import Footer from './components/Footer20';
import ProductCard from './components/ProductCard';
import Button from './components/Button';

const Shop = () => {
    const [selectedCategory, setSelectedCategory] = useState('all');
    const [searchQuery, setSearchQuery] = useState('');
    const [sortBy, setSortBy] = useState('featured');

    // Sample product data - in real app, this would come from API/backend
    const products = [
        { id: 1, name: 'Abstract Expression Tee', price: 1299, category: 'abstract', tag: 'New', image: 'https://images.unsplash.com/photo-1521572163474-6864f9cf17ab?w=400&h=500&fit=crop' },
        { id: 2, name: 'Urban Nights Hoodie', price: 2499, category: 'urban', tag: 'Popular', image: 'https://images.unsplash.com/photo-1556821840-3a63f95609a7?w=400&h=500&fit=crop' },
        { id: 3, name: 'Nature Print Sweatshirt', price: 1899, category: 'nature', image: 'https://images.unsplash.com/photo-1578587018452-892bacefd3f2?w=400&h=500&fit=crop' },
        { id: 4, name: 'Geometric Pattern Jacket', price: 3499, category: 'abstract', tag: 'Limited', image: 'https://images.unsplash.com/photo-1551028719-00167b16eac5?w=400&h=500&fit=crop' },
        { id: 5, name: 'City Lights Tee', price: 1199, category: 'urban', image: 'https://images.unsplash.com/photo-1583743814966-8936f5b7be1a?w=400&h=500&fit=crop' },
        { id: 6, name: 'Forest Dreams Hoodie', price: 2299, category: 'nature', tag: 'New', image: 'https://images.unsplash.com/photo-1556821840-3a63f95609a7?w=400&h=500&fit=crop' },
        { id: 7, name: 'Splash Art Tank Top', price: 999, category: 'abstract', image: 'https://images.unsplash.com/photo-1622445275576-721325763afe?w=400&h=500&fit=crop' },
        { id: 8, name: 'Street Style Bomber', price: 3999, category: 'urban', tag: 'Popular', image: 'https://images.unsplash.com/photo-1591047139829-d91aecb6caea?w=400&h=500&fit=crop' },
        { id: 9, name: 'Mountain Peak Pullover', price: 2099, category: 'nature', image: 'https://images.unsplash.com/photo-1620799140408-edc6dcb6d633?w=400&h=500&fit=crop' },
        { id: 10, name: 'Abstract Lines Cardigan', price: 2799, category: 'abstract', tag: 'New', image: 'https://images.unsplash.com/photo-1434389677669-e08b4cac3105?w=400&h=500&fit=crop' },
        { id: 11, name: 'Metro Life Jacket', price: 3299, category: 'urban', image: 'https://images.unsplash.com/photo-1551028719-00167b16eac5?w=400&h=500&fit=crop' },
        { id: 12, name: 'Ocean Wave Shirt', price: 1599, category: 'nature', tag: 'Limited', image: 'https://images.unsplash.com/photo-1596755094514-f87e34085b2c?w=400&h=500&fit=crop' },
    ];

    const categories = [
        { id: 'all', name: 'All Collections', count: products.length },
        { id: 'abstract', name: 'Abstract', count: products.filter(p => p.category === 'abstract').length },
        { id: 'urban', name: 'Urban', count: products.filter(p => p.category === 'urban').length },
        { id: 'nature', name: 'Nature', count: products.filter(p => p.category === 'nature').length },
    ];

    // Filter and sort products
    const filteredProducts = products
        .filter(product => {
            const matchesCategory = selectedCategory === 'all' || product.category === selectedCategory;
            const matchesSearch = product.name.toLowerCase().includes(searchQuery.toLowerCase());
            return matchesCategory && matchesSearch;
        })
        .sort((a, b) => {
            if (sortBy === 'price-low') return a.price - b.price;
            if (sortBy === 'price-high') return b.price - a.price;
            if (sortBy === 'name') return a.name.localeCompare(b.name);
            return 0; // featured/default
        });

    return (
        <div className="min-h-screen bg-gradient-to-br from-amber-50 via-orange-50 to-yellow-50">
            <Header cartCount={3} />

            {/* Hero Section */}
            <section className="bg-white py-12 lg:py-16">
                <div className="container mx-auto px-4 lg:px-8">
                    <div className="text-center max-w-3xl mx-auto">
                        <h1 className="text-4xl lg:text-6xl font-bold bg-gradient-to-r from-amber-600 to-orange-600 bg-clip-text text-transparent mb-4">
                            Creative Collections
                        </h1>
                        <p className="text-gray-600 text-lg">
                            Explore our curated selection of artistic fashion pieces. Each design tells a unique story.
                        </p>
                    </div>
                </div>
            </section>

            {/* Filters and Search */}
            <section className="py-8 lg:py-12">
                <div className="container mx-auto px-4 lg:px-8">
                    {/* Search Bar */}
                    <div className="mb-8">
                        <div className="relative max-w-xl mx-auto">
                            <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                            <input
                                type="text"
                                placeholder="Search for creative designs..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className="w-full pl-12 pr-4 py-4 bg-white rounded-full shadow-lg focus:outline-none focus:ring-2 focus:ring-orange-500 border border-orange-200"
                            />
                        </div>
                    </div>

                    {/* Category Filters */}
                    <div className="flex flex-wrap justify-center gap-3 mb-8">
                        {categories.map(category => (
                            <button
                                key={category.id}
                                onClick={() => setSelectedCategory(category.id)}
                                className={`px-6 py-3 rounded-full font-medium transition-all duration-300 ${
                                    selectedCategory === category.id
                                        ? 'bg-gradient-to-r from-orange-500 to-amber-500 text-white shadow-lg scale-105'
                                        : 'bg-white text-gray-700 hover:bg-orange-50 border border-orange-200'
                                }`}
                            >
                                {category.name}
                                <span className="ml-2 text-sm opacity-75">({category.count})</span>
                            </button>
                        ))}
                    </div>

                    {/* Sort Options */}
                    <div className="flex justify-between items-center mb-8 flex-wrap gap-4">
                        <p className="text-gray-600">
                            Showing <span className="font-semibold text-orange-600">{filteredProducts.length}</span> products
                        </p>
                        <div className="flex items-center gap-3">
                            <SlidersHorizontal className="text-gray-500" size={20} />
                            <select
                                value={sortBy}
                                onChange={(e) => setSortBy(e.target.value)}
                                className="px-4 py-2 bg-white rounded-full border border-orange-200 focus:outline-none focus:ring-2 focus:ring-orange-500 text-gray-700"
                            >
                                <option value="featured">Featured</option>
                                <option value="price-low">Price: Low to High</option>
                                <option value="price-high">Price: High to Low</option>
                                <option value="name">Name: A to Z</option>
                            </select>
                        </div>
                    </div>

                    {/* Products Grid */}
                    {filteredProducts.length > 0 ? (
                        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 lg:gap-6">
                            {filteredProducts.map(product => (
                                <ProductCard key={product.id} product={product} />
                            ))}
                        </div>
                    ) : (
                        <div className="text-center py-16">
                            <p className="text-gray-500 text-lg mb-4">No products found matching your criteria</p>
                            <Button variant="primary" onClick={() => { setSearchQuery(''); setSelectedCategory('all'); }}>
                                Clear Filters
                            </Button>
                        </div>
                    )}
                </div>
            </section>

            {/* Call to Action */}
            <section className="bg-white py-12 lg:py-16">
                <div className="container mx-auto px-4 lg:px-8 text-center">
                    <h2 className="text-3xl lg:text-4xl font-bold bg-gradient-to-r from-amber-600 to-orange-600 bg-clip-text text-transparent mb-4">
                        Can't Find What You're Looking For?
                    </h2>
                    <p className="text-gray-600 mb-8 max-w-2xl mx-auto">
                        Commission a custom piece or explore our bespoke design services
                    </p>
                    <Button variant="primary" size="lg">
                        Custom Design Request
                    </Button>
                </div>
            </section>

            <Footer />
        </div>
    );
};

export default Shop;
